import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, BarChart3, Compass, MapPin, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SURFACE_LABEL = {
  city_guide: 'City Guide',
  blueprint: 'Blueprint',
  events_empty: 'Events (empty)',
  adventures_public: 'Adventures (public)',
  adventures_portal: 'Adventures (portal)',
  unknown: 'Unknown',
};

/**
 * Admin → Analytics → Affiliate Clicks (iter 78).
 *
 * Read /api/admin/affiliates/clicks?days=30 and surface three rollups:
 * by surface, by city, by day. Plus a 50-row recent feed for spot checks.
 */
const StatTile = ({ label, value, testId }) => (
  <div className="bg-white border border-[#EFE6CF] rounded-2xl p-5" data-testid={testId}>
    <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-2">{label}</p>
    <p className="font-display italic text-4xl text-[#0A1F2E] leading-none">{value}</p>
  </div>
);

const BarRow = ({ label, count, max, testId }) => {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <li className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#0A1F2E] truncate pr-2">{label}</span>
        <span className="font-mono text-[#1A5C6E]">{count}</span>
      </div>
      <div className="h-2 bg-[#EFE6CF] rounded-full overflow-hidden">
        <div className="h-full bg-[#2CA8A0] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
};

const AdminAffiliateClicks = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/affiliates/clicks?days=${days}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not load click data');
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const surfaces = data?.by_surface || [];
  const cities = data?.by_city || [];
  const byDay = data?.by_day || [];
  const recent = data?.recent || [];

  const surfaceMax = surfaces.reduce((m, r) => Math.max(m, r.count), 0);
  const cityMax = cities.reduce((m, r) => Math.max(m, r.count), 0);

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="admin-affiliate-clicks">
        <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
              Admin · Analytics
            </p>
            <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
              Affiliate clicks
            </h1>
            <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
              Tracks every Viator card click across the three surfaces. Anonymous - no PII captured.
            </p>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] self-start md:self-auto"
            data-testid="affiliate-clicks-range"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
        </header>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
          </div>
        )}
        {!loading && error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 rounded-xl text-sm" data-testid="affiliate-clicks-error">
            {error}
          </div>
        )}
        {!loading && !error && data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <StatTile label="Total clicks" value={data.total} testId="stat-total" />
              <StatTile label="Surfaces tracked" value={surfaces.length} testId="stat-surfaces" />
              <StatTile label="Cities with clicks" value={cities.length} testId="stat-cities" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5" data-testid="rollup-by-surface">
                <h2 className="font-display italic text-xl text-[#0A1F2E] mb-1 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#1A5C6E]" /> By surface
                </h2>
                <p className="text-xs text-[#0A1F2E]/55 mb-4">Where the clicks happened.</p>
                {surfaces.length === 0 ? (
                  <p className="text-sm text-[#0A1F2E]/55">No clicks yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {surfaces.map((r) => (
                      <BarRow
                        key={r.surface}
                        label={SURFACE_LABEL[r.surface] || r.surface}
                        count={r.count}
                        max={surfaceMax}
                        testId={`row-surface-${r.surface}`}
                      />
                    ))}
                  </ul>
                )}
              </section>

              <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5" data-testid="rollup-by-city">
                <h2 className="font-display italic text-xl text-[#0A1F2E] mb-1 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#1A5C6E]" /> By city
                </h2>
                <p className="text-xs text-[#0A1F2E]/55 mb-4">Only city-guide clicks carry a city.</p>
                {cities.length === 0 ? (
                  <p className="text-sm text-[#0A1F2E]/55">No city-attributed clicks yet.</p>
                ) : (
                  <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {cities.map((r) => (
                      <BarRow
                        key={r.city}
                        label={r.city}
                        count={r.count}
                        max={cityMax}
                        testId={`row-city-${r.city}`}
                      />
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5 mb-6" data-testid="rollup-by-day">
              <h2 className="font-display italic text-xl text-[#0A1F2E] mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#1A5C6E]" /> By day
              </h2>
              {byDay.length === 0 ? (
                <p className="text-sm text-[#0A1F2E]/55">No clicks yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[#0A1F2E]/55 font-mono uppercase text-[10px] tracking-[0.15em]">
                      <tr><th className="text-left py-2 pr-4">Day</th><th className="text-right py-2">Clicks</th></tr>
                    </thead>
                    <tbody className="divide-y divide-[#EFE6CF]">
                      {byDay.slice().reverse().slice(0, 20).map((r) => (
                        <tr key={r.day}>
                          <td className="py-2 pr-4 text-[#0A1F2E]">{r.day}</td>
                          <td className="py-2 text-right font-mono text-[#1A5C6E]">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5" data-testid="rollup-recent">
              <h2 className="font-display italic text-xl text-[#0A1F2E] mb-1 flex items-center gap-2">
                <Compass className="w-4 h-4 text-[#1A5C6E]" /> Recent clicks
              </h2>
              <p className="text-xs text-[#0A1F2E]/55 mb-4">Last {Math.min(50, recent.length)} clicks.</p>
              {recent.length === 0 ? (
                <p className="text-sm text-[#0A1F2E]/55">No clicks yet.</p>
              ) : (
                <ul className="divide-y divide-[#EFE6CF] text-sm">
                  {recent.map((r) => (
                    <li key={r.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`recent-row-${r.id}`}>
                      <span className="font-mono text-[10px] tracking-[0.1em] uppercase bg-[#F4ECD8] text-[#1A5C6E] px-2 py-0.5 rounded-full">
                        {SURFACE_LABEL[r.surface] || r.surface}
                      </span>
                      {r.city_name && <span className="text-[#0A1F2E]/85">{r.city_name}</span>}
                      <span className="text-[#0A1F2E]/55">{r.user_role || 'anonymous'}</span>
                      <span className="text-[#0A1F2E]/45 ml-auto">{new Date(r.ts).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAffiliateClicks;
